import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/auth';
import {
  getGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  getAssignableUsers,
} from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: number;
  name?: string;
  username?: string;
  email?: string;
  role: string;
}

interface Group {
  id: number;
  name: string;
  description?: string;
  createdBy: {
    id: number;
    name?: string;
    username?: string;
    email?: string;
  };
  createdAt: string;
  members: {
    id: number;
    name?: string;
    username?: string;
    email?: string;
  }[];
}

export default function Groups() {
  const { user } = useAuthStore();
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadGroups();
    if (user?.role === 'boss' || user?.role === 'manager') {
      loadUsers();
    }
  }, []);

  const loadGroups = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
    } catch (err: any) {
      setError(err.message || '無法載入群組');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getAssignableUsers();
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group: Group) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      description: group.description,
      memberIds: group.members.map((m) => m.id),
    });
    setModalVisible(true);
  };

  const handleDelete = async (groupId: number) => {
    try {
      await deleteGroup(groupId);
      await loadGroups();
    } catch (err: any) {
      setError(err.message || '刪除失敗');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    setError('');

    try {
      if (editingGroup) {
        await updateGroup(editingGroup.id, values);
      } else {
        await createGroup(values);
      }
      setModalVisible(false);
      await loadGroups();
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const canManageGroups = user?.role === 'boss' || user?.role === 'manager';

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <TeamOutlined /> 群組管理
        </Title>
        {canManageGroups && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            建立群組
          </Button>
        )}
      </div>

      {error && (
        <Alert title={error} type="error" showIcon closable onClose={() => setError('')} />
      )}

      {groups.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Text type="secondary">目前沒有群組</Text>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {groups.map((group) => (
            <Col xs={24} sm={12} lg={8} key={group.id}>
              <Card
                title={group.name}
                extra={
                  (group.createdBy.id === user?.id || user?.role === 'boss') && (
                    <Space>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(group)}
                      />
                      <Popconfirm
                        title="確定要刪除此群組嗎？"
                        onConfirm={() => handleDelete(group.id)}
                        okText="確定"
                        cancelText="取消"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  )
                }
              >
                {group.description && (
                  <Text type="secondary">{group.description}</Text>
                )}
                <div style={{ marginTop: 16 }}>
                  <Space>
                    <UserOutlined />
                    <Text type="secondary">
                      建立者: {getUserDisplayName(group.createdBy)} · {group.members.length} 位成員
                    </Text>
                  </Space>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Space size="small" wrap>
                    {group.members.map((member) => (
                      <Tag key={member.id}>
                        {getUserDisplayName(member)}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editingGroup ? '編輯群組' : '建立群組'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="群組名稱"
            rules={[{ required: true, message: '請輸入群組名稱' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input placeholder="選填" />
          </Form.Item>

          <Form.Item name="memberIds" label="成員">
            <Select
              mode="multiple"
              placeholder="選擇成員"
              style={{ width: '100%' }}
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {getUserDisplayName(user)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingGroup ? '更新' : '建立'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
